<template>
  <div>
    <v-layout row wrap class="ml-3">
      <v-flex >
        <span class="subheading font-weight-bold">Assessment will end forcibly after</span>
        <v-chip color="primary" text-color="primary" class="ml-2 mb-2" outlined>
          {{ modified | timeDuration }}
          <v-icon right>timer</v-icon>
        </v-chip>
        <v-select
          :items="options"
          label="Modify Assessment Duration"
          v-model="selected"
        >
          <template v-slot:item="{ item }">
            <div>
              <p class="subheading font-weight-bold ma-0">{{ item.text }}</p>
              <p class="body-2 font-weight-light font-italic d-block ma-0">{{ item.tooltip }}</p>
            </div>
          </template>
        </v-select>
        <v-text-field v-if="isCustom"
          label="Duration Modifier"
          type="number"
          v-model="custom"
          :rules="rules"
        />
      </v-flex>
      <v-flex xs6>
        <span class="subheading font-weight-bold ml-4">Task breakdown</span>
        <dl class="dl-card-align dl-breakdown ml-4">
          <div v-for="taskType in taskTypes" :key="`task-${taskType.id}`">
            <dt>{{ taskType.short_code }}</dt>
            <dd>{{ taskType.duration * modifier | timeDuration }}</dd>
          </div>
        </dl>
      </v-flex>
      <v-flex v-if="messages.length > 0" xs12>
        <v-messages :messages="messages" />
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import VMessages from '@/components/Messages'

export default {
  name: 'AssessmentModifierField',
  components: {
    VMessages
  },
  props: {
    /**
     * Total duration which will be modified
     */
    taskIds: {
      type: Array,
      required: true
    },
    /**
     * The user value
     */
    value: {
      required: true
    },
    defaultModifier: {
      type: Number,
      required: false,
      default: 1
    }
  },
  /**
   * Bind the header index to the v-model and emit change event
   */
  model: {
    prop: 'value',
    event: 'change'
  },
  data () {
    return {
      /**
       * Custom value
       */
      custom: '1.0',
      /**
       * The selected predefined option
       */
      selected: undefined,
      /**
       * Predefined options
       */
      options: [
        {
          value: 1,
          text: 'Default assessment duration',
          tooltip: 'Choose this to take the assessment for as long as a typical end user would'
        },
        {
          value: 1.5,
          text: 'Moderate extra time',
          tooltip: 'Choose this to allow you to stay in the assessment for a few hours'
        },
        {
          value: 10,
          text: 'Significant extra time',
          tooltip: 'Choose this to allow you to stay in the assessment for as long as you should ever need to'
        },
        {
          value: null,
          text: 'Custom extra time',
          tooltip: 'Choose this to input your own custom modifier'
        }
      ]
    }
  },
  computed: {
    /**
     * Get the modifier value
     */
    modifier () {
      if (this.selected === undefined) {
        return null
      } else if (this.selected !== null) {
        return this.selected
      } else {
        return parseFloat(this.custom)
      }
    },
    /**
     * Is custom value selected
     */
    isCustom () {
      return this.selected === null
    },
    /**
     * Rules for the custom value field
     */
    rules () {
      return [
        () => !isNaN(this.modifier) || 'Incorrect value',
        () => this.modifier > 0 || 'Please provide value larger than 0',
        () => !!this.modifier || 'Required'
      ]
    },
    /**
     * Messages to display according to modifier value
     */
    messages () {
      let messages = []

      if (this.modifier > 0 && this.modifier < 1) {
        messages.push({
          color: 'warning',
          icon: 'priority_high',
          text: 'This will provide LESS time in the assessment than a typical user would see.'
        })
      }

      return messages
    },
    taskTypes () {
      return this.$store.getters['tasks/taskTypes'](this.taskIds)
    },
    duration () {
      return this.$store.getters['tasks/tasksDuration'](this.taskIds)
    },
    modified () {
      return Math.floor(this.duration * this.modifier)
    }
  },
  watch: {
    /**
     * v-model handler
     */
    modifier: {
      handler () {
        if (this.modifier === null) {
          if (this.value !== null) {
            this.populateProvided()
          } else {
            this.populateDefault()
          }
        }

        this.$emit('change', this.modifier)
      },
      immediate: true
    }
  },
  methods: {
    populateDefault () {
      const predefined = this.options.find(option => option.value === this.defaultModifier)

      if (predefined) {
        this.selected = predefined.value
      } else {
        this.selected = null
        this.custom = this.defaultModifier
      }
    },
    populateProvided () {
      const provided = this.options.find(option => option.value === this.value)

      if (provided) {
        this.selected = provided.value
      } else {
        this.selected = null
        this.custom = this.value
      }
    }
  }
}
</script>

<style lang="sass">
.dl-breakdown
  text-align: right
  dt
    width: 50px
</style>
